import React, { useState, useEffect } from 'react';
import { db, auth } from '../firebase';
import { collection, getDocs, addDoc, updateDoc, deleteDoc, doc, serverTimestamp } from 'firebase/firestore';
import { createUserWithEmailAndPassword } from 'firebase/auth';

const ROLES = { manager: 'Manager', staff: 'Staff' };

export default function TeamMembersTab({ user, centerData }) {
  const [members, setMembers]   = useState([]);
  const [loading, setLoading]   = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving]     = useState(false);
  const [form, setForm] = useState({ name: '', email: '', role: 'staff', title: '' });
  const [msg, setMsg]   = useState('');

  useEffect(() => { loadMembers(); }, [user.uid]);

  const loadMembers = async () => {
    try {
      const snap = await getDocs(collection(db, 'serviceCenters', user.uid, 'teamMembers'));
      setMembers(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const invite = async () => {
    if (!form.name || !form.email) { setMsg('Name and email are required.'); return; }
    setSaving(true); setMsg('');
    try {
      await addDoc(collection(db, 'serviceCenters', user.uid, 'teamMembers'), {
        name:         form.name.trim(),
        email:        form.email.trim().toLowerCase(),
        role:         form.role,
        title:        form.title.trim(),
        status:       'invited',
        invitedAt:    serverTimestamp(),
        invitedBy:    user.uid,
      });
      setMsg('Invitation sent successfully!');
      setForm({ name: '', email: '', role: 'staff', title: '' });
      setShowForm(false);
      loadMembers();
    } catch (e) { setMsg('Error: ' + e.message); }
    finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm('Remove this team member?')) return;
    await deleteDoc(doc(db, 'serviceCenters', user.uid, 'teamMembers', id));
    loadMembers();
  };

  const iS = { width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid #e5e7eb', fontSize: 14, color: '#111827', background: '#f9fafb', outline: 'none', boxSizing: 'border-box', marginBottom: 12 };
  const lS = { display: 'block', fontSize: 12, fontWeight: 700, color: '#6b7280', marginBottom: 4, textTransform: 'uppercase', letterSpacing: 0.5 };

  return (
    <div style={{ padding: 32 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 28 }}>
        <div>
          <h1 style={{ fontSize: 24, fontWeight: 900 }}>Team Members</h1>
          <p style={{ color: '#6b7280', marginTop: 4 }}>Manage staff who work with CocoWash vehicles</p>
        </div>
        <button onClick={() => setShowForm(true)} style={{ background: '#F97316', color: '#fff', border: 'none', borderRadius: 10, padding: '10px 20px', fontWeight: 700, cursor: 'pointer', fontSize: 14 }}>+ Invite Member</button>
      </div>

      {msg && <div style={{ background: msg.includes('success') ? '#f0fdf4' : '#fef2f2', color: msg.includes('success') ? '#16a34a' : '#dc2626', padding: 12, borderRadius: 10, marginBottom: 16, fontSize: 13 }}>{msg}</div>}

      {loading ? <p style={{ color: '#6b7280' }}>Loading...</p> : members.length === 0 ? (
        <div style={{ background: '#fff', borderRadius: 16, padding: 48, textAlign: 'center', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
          <div style={{ fontSize: 48, marginBottom: 16 }}>👥</div>
          <h3 style={{ fontSize: 18, fontWeight: 800, marginBottom: 8 }}>No team members yet</h3>
          <p style={{ color: '#6b7280', marginBottom: 20 }}>Invite staff to help manage CocoWash jobs at your facility</p>
          <button onClick={() => setShowForm(true)} style={{ background: '#F97316', color: '#fff', border: 'none', borderRadius: 10, padding: '12px 24px', fontWeight: 700, cursor: 'pointer' }}>Invite First Member</button>
        </div>
      ) : (
        <div style={{ display: 'grid', gap: 12 }}>
          {members.map(m => (
            <div key={m.id} style={{ background: '#fff', borderRadius: 14, padding: 18, boxShadow: '0 1px 4px rgba(0,0,0,0.06)', display: 'flex', alignItems: 'center', gap: 16 }}>
              <div style={{ width: 44, height: 44, borderRadius: 22, background: '#fff7ed', border: '2px solid #F97316', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 900, color: '#F97316', fontSize: 18, flexShrink: 0 }}>
                {(m.name || 'M').charAt(0).toUpperCase()}
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 15, fontWeight: 800 }}>{m.name}</div>
                <div style={{ fontSize: 12, color: '#6b7280' }}>{m.email} · {m.title || ROLES[m.role] || m.role}</div>
              </div>
              <div style={{ display: 'flex', align: 'center', gap: 10 }}>
                <span style={{ fontSize: 11, fontWeight: 700, padding: '3px 10px', borderRadius: 20, background: m.status === 'active' ? '#f0fdf4' : '#fffbeb', color: m.status === 'active' ? '#16a34a' : '#d97706' }}>
                  {m.status === 'active' ? 'Active' : 'Invited'}
                </span>
                <button onClick={() => remove(m.id)} style={{ padding: '6px 12px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, fontSize: 12, fontWeight: 700, cursor: 'pointer', color: '#dc2626' }}>Remove</button>
              </div>
            </div>
          ))}
        </div>
      )}

      {showForm && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 20 }}>
          <div style={{ background: '#fff', borderRadius: 20, padding: 32, width: '100%', maxWidth: 440 }}>
            <h2 style={{ fontSize: 20, fontWeight: 900, marginBottom: 24 }}>Invite Team Member</h2>
            <label style={lS}>Full Name *</label>
            <input style={iS} value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="e.g. James Wilson" />
            <label style={lS}>Email Address *</label>
            <input style={iS} type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} placeholder="james@yourbusiness.com" />
            <label style={lS}>Role</label>
            <select style={{ ...iS }} value={form.role} onChange={e => setForm(f => ({ ...f, role: e.target.value }))}>
              <option value="staff">Staff</option>
              <option value="manager">Manager</option>
            </select>
            <label style={lS}>Job Title (optional)</label>
            <input style={iS} value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} placeholder="e.g. Lead Detailer, Service Manager" />
            <div style={{ display: 'flex', gap: 12, marginTop: 8 }}>
              <button onClick={() => setShowForm(false)} style={{ flex: 1, padding: '12px', background: '#f3f4f6', border: 'none', borderRadius: 10, fontWeight: 600, cursor: 'pointer' }}>Cancel</button>
              <button onClick={invite} disabled={saving} style={{ flex: 1, padding: '12px', background: '#F97316', color: '#fff', border: 'none', borderRadius: 10, fontWeight: 700, cursor: 'pointer' }}>{saving ? 'Sending...' : 'Send Invitation'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}